import { useState } from 'react';
import { Loader2, Sparkles, AlertTriangle, CheckCircle2 } from 'lucide-react';
import Button from './ui/Button';
import Badge from './ui/Badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/Card';
import api from '../lib/api';

export interface KOLSuggestion {
  kolId: string;
  kolName: string;
  algorithmScore: number;
  aiScore: number;
  reasoning: string;
  risks: string[];
  matchReasons: string[];
  tags: string[];
  totalFollowers: number;
  avgEngagement: number;
  recommended: boolean;
}

interface AIKOLSuggestionsProps {
  jobId: string;
  onSelectKOLs?: (kolIds: string[]) => void;
  compact?: boolean;
}

export function AIKOLSuggestions({ jobId, onSelectKOLs, compact = false }: AIKOLSuggestionsProps) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<{
    summary: string;
    suggestions: KOLSuggestion[];
  } | null>(null);

  const loadSuggestions = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await api.post('/ai/broadcast/rerank', { jobId, maxCandidates: 15 });
      const kols = response.data.kols || [];
      const suggestions: KOLSuggestion[] = kols.slice(0, 10).map((kol: any, index: number) => {
        const platforms = kol.platforms || {};
        let totalFollowers = 0;
        let totalEngagement = 0;
        let count = 0;
        Object.values(platforms).forEach((p: any) => {
          if (p?.followers) {
            totalFollowers += p.followers;
            totalEngagement += p.engagementRate || 0;
            count++;
          }
        });
        return {
          kolId: kol.id,
          kolName: kol.name,
          algorithmScore: kol.matchScore ?? 0,
          aiScore: kol.aiScore ?? kol.finalScore ?? kol.matchScore ?? 0,
          reasoning: (kol.aiReasons || []).join(' ') || (kol.matchReasons || []).slice(0, 2).join('; ') || 'Good algorithmic match',
          risks: kol.riskFlags || [],
          matchReasons: kol.matchReasons || [],
          tags: (kol.tags || []).map((t: any) => t.tag?.name || t).filter(Boolean),
          totalFollowers,
          avgEngagement: count > 0 ? parseFloat((totalEngagement / count).toFixed(1)) : 0,
          recommended: index < 3 && (kol.aiAvailable !== false),
        };
      });
      const summary = response.data.aiAvailable
        ? `AI reranked ${kols.length} KOL candidates using blended algorithm + AI scores.`
        : `Showing top ${kols.length} algorithm matches (AI rerank unavailable — check DEEPSEEK_API_KEY).`;
      setResult({ summary, suggestions });
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load AI suggestions');
    } finally {
      setLoading(false);
    }
  };

  const applyRecommended = () => {
    if (!result || !onSelectKOLs) return;
    const ids = result.suggestions.filter((s) => s.recommended).map((s) => s.kolId);
    onSelectKOLs(ids.length > 0 ? ids : result.suggestions.slice(0, 3).map((s) => s.kolId));
  };

  if (compact && !result) {
    return (
      <Button onClick={loadSuggestions} disabled={loading} variant="outline" size="sm">
        {loading ? (
          <Loader2 className="w-4 h-4 animate-spin mr-1" />
        ) : (
          <Sparkles className="w-4 h-4 mr-1" />
        )}
        AI Suggestions
      </Button>
    );
  }

  return (
    <Card className="border-indigo-200 bg-indigo-50/50">
      <CardHeader className={compact ? 'pb-2' : undefined}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-indigo-600" />
            <CardTitle className="text-lg">AI KOL Recommendations</CardTitle>
          </div>
          {!result && (
            <Button onClick={loadSuggestions} disabled={loading} size="sm" className="bg-indigo-600 hover:bg-indigo-700">
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin mr-1" />
                  Analyzing...
                </>
              ) : (
                <>
                  <Sparkles className="w-4 h-4 mr-1" />
                  Get AI Suggestions
                </>
              )}
            </Button>
          )}
        </div>
        <CardDescription>
          AI analyzes algorithm matches and provides reasoning, risk flags, and recommendations
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {error && (
          <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700">{error}</div>
        )}

        {result && (
          <>
            <div className="rounded-lg border bg-white p-3 text-sm text-gray-700">{result.summary}</div>

            {result.suggestions.length === 0 ? (
              <p className="text-sm text-gray-500">No matching KOLs found. Try adjusting job criteria.</p>
            ) : (
              <div className="space-y-3 max-h-80 overflow-y-auto">
                {result.suggestions.map((s) => (
                  <div
                    key={s.kolId}
                    className={`rounded-lg border p-3 bg-white ${s.recommended ? 'border-indigo-300 ring-1 ring-indigo-200' : ''}`}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-gray-900">{s.kolName}</span>
                          {s.recommended && (
                            <Badge variant="success" className="text-xs">
                              <CheckCircle2 className="w-3 h-3 mr-1" />
                              Recommended
                            </Badge>
                          )}
                        </div>
                        <p className="text-xs text-gray-500 mt-0.5">
                          {s.tags.join(', ')} · {s.totalFollowers.toLocaleString()} followers · {s.avgEngagement}% eng.
                        </p>
                      </div>
                      <div className="text-right shrink-0">
                        <div className="text-xs text-gray-500">AI Score</div>
                        <div className="font-bold text-indigo-600">{s.aiScore}</div>
                        <div className="text-xs text-gray-400">Algo: {s.algorithmScore}</div>
                      </div>
                    </div>
                    <p className="text-sm text-gray-700 mt-2">{s.reasoning}</p>
                    {s.risks.length > 0 && (
                      <div className="mt-2 flex flex-wrap gap-1">
                        {s.risks.map((risk, i) => (
                          <span
                            key={i}
                            className="inline-flex items-center gap-1 text-xs bg-amber-50 text-amber-800 border border-amber-200 rounded px-2 py-0.5"
                          >
                            <AlertTriangle className="w-3 h-3" />
                            {risk}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}

            <div className="flex gap-2">
              {onSelectKOLs && result.suggestions.length > 0 && (
                <Button onClick={applyRecommended} className="flex-1 bg-indigo-600 hover:bg-indigo-700">
                  Select AI Recommended KOLs
                </Button>
              )}
              <Button onClick={loadSuggestions} variant="outline" disabled={loading}>
                Refresh
              </Button>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}
